import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { apiGetUserUiProps, apiPutUserUiProps } from '../utils/api';

const PreferencesContext = createContext();
const STORAGE_KEY = 'klaes_preferences';

/* =========================
   DEFAULTS
========================= */

const DEFAULT_PREFS = {
  accentColor: '#6366f1',
  fontSize: 100,
  theme: null, // wichtig: null = kein Override
  widgets: {
    order: ['film-favs', 'music-favs', 'book-favs', 'family-calendar', 'family-chat', 'schwurbler-newsfeed'],
    visibility: {
      'film-favs': true,
      'music-favs': true,
      'book-favs': true,
      'family-calendar': true,
      'family-chat': true,
      'schwurbler-newsfeed': true
    }
  }
};

/* =========================
   BASE THEMES
========================= */

const LIGHT_THEME = {
  bgPrimary: '#ffffff',
  bgSecondary: '#f1f5f9',
  textPrimary: '#0f172a',
  buttonBg: '#6366f1',
  buttonText: '#ffffff'
};

const DARK_THEME = {
  bgPrimary: '#050816',
  bgSecondary: '#0f172a',
  textPrimary: '#f8fafc',
  buttonBg: '#6366f1',
  buttonText: '#ffffff'
};

/* =========================
   PROVIDER
========================= */

export const PreferencesProvider = ({ children }) => {
  const { token } = useAuth();

  const [preferences, setPreferences] = useState(DEFAULT_PREFS);
  const [loading, setLoading] = useState(true);

  const [themeMode, setThemeMode] = useState(
    localStorage.getItem('theme') || 'dark'
  );

  /* =========================
     CSS APPLY HELPERS
  ========================= */

  const applyThemeToCss = (prefs, mode) => {
    const baseTheme = mode === 'dark' ? DARK_THEME : LIGHT_THEME;

    // Custom override (falls vorhanden)
    const custom = prefs.theme || {};

    const finalTheme = {
      ...baseTheme,
      ...custom
    };

    document.body.style.setProperty('--bg-primary', finalTheme.bgPrimary);
    document.body.style.setProperty('--bg-secondary', finalTheme.bgSecondary);
    document.body.style.setProperty('--text-primary', finalTheme.textPrimary);
    document.body.style.setProperty('--button-bg', finalTheme.buttonBg);
    document.body.style.setProperty('--button-text', finalTheme.buttonText);
  };

  const applyPreferencesToCss = (prefs, mode = themeMode) => {
    // Accent
    const accent = prefs.accentColor || DEFAULT_PREFS.accentColor;

    document.body.style.setProperty('--user-accent', accent);
    document.body.style.setProperty('--user-accent-light', accent);
    document.body.style.setProperty('--user-accent-dark', accent);

    // Font size
    const baseSize = (prefs.fontSize / 100) * 16;
    document.documentElement.style.setProperty('--base-font-size', `${baseSize}px`);

    // Theme
    applyThemeToCss(prefs, mode);
  };

  /* =========================
     LOAD FROM BACKEND
  ========================= */

  useEffect(() => {
    if (!token) {
      applyPreferencesToCss(DEFAULT_PREFS);
      setLoading(false);
      return;
    }

    const load = async () => {
      try {
        const data = await apiGetUserUiProps(token);
        const remotePrefs = data.ui_props || {};
        const remoteWidgets = remotePrefs.widgets || {};
        const savedWidgetOrder = Array.isArray(remoteWidgets.order) ? remoteWidgets.order : [];
        const widgetOrder = [...new Set([...savedWidgetOrder, ...DEFAULT_PREFS.widgets.order])];
        const bookIndex = widgetOrder.indexOf('book-favs');
        const musicIndex = widgetOrder.indexOf('music-favs');
        if (bookIndex === widgetOrder.length - 1 && musicIndex !== -1 && bookIndex !== musicIndex + 1) {
          widgetOrder.splice(bookIndex, 1);
          widgetOrder.splice(musicIndex + 1, 0, 'book-favs');
        }

        const merged = {
          ...DEFAULT_PREFS,
          ...remotePrefs,
          widgets: {
            ...DEFAULT_PREFS.widgets,
            ...remoteWidgets,
            order: widgetOrder,
            visibility: {
              ...DEFAULT_PREFS.widgets.visibility,
              ...(remoteWidgets.visibility || {})
            }
          }
        };

        // wichtig: kein null-Müll mehr
        merged.theme = remotePrefs.theme || null;

        setPreferences(merged);
        applyPreferencesToCss(merged, themeMode);

      } catch (err) {
        console.error('Fehler beim Laden der Preferences', err);
        applyPreferencesToCss(DEFAULT_PREFS, themeMode);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [token]);

  /* =========================
     REACTIVE APPLY
  ========================= */

  useEffect(() => {
    applyPreferencesToCss(preferences, themeMode);
  }, [themeMode, preferences]);

  /* =========================
     SAVE
  ========================= */

  const savePreferences = async (newPrefs) => {
    setPreferences(newPrefs);
    applyPreferencesToCss(newPrefs, themeMode);

    localStorage.setItem(STORAGE_KEY, JSON.stringify(newPrefs));

    if (token) {
      try {
        await apiPutUserUiProps(token, newPrefs);
      } catch (err) {
        console.error('Speichern fehlgeschlagen', err);
      }
    }
  };

  /* =========================
     UPDATE SINGLE VALUE
  ========================= */

  const updatePreference = (key, value) => {
    setPreferences(prev => {
      const updated = { ...prev, [key]: value };
      applyPreferencesToCss(updated, themeMode);
      return updated;
    });
  };

  /* =========================
     RESET
  ========================= */

  const resetToDefault = async () => {
    const reset = {
      ...DEFAULT_PREFS,
      theme: null
    };

    await savePreferences(reset);
  };

  /* =========================
     LIGHT / DARK TOGGLE
  ========================= */

  const toggleThemeMode = () => {
    const newMode = themeMode === 'dark' ? 'light' : 'dark';

    setThemeMode(newMode);
    localStorage.setItem('theme', newMode);

    document.body.dataset.theme = newMode;

    // sofort neu anwenden
    applyPreferencesToCss(preferences, newMode);
  };

  /* =========================
     CONTEXT VALUE
  ========================= */

  return (
    <PreferencesContext.Provider
      value={{
        preferences,
        savePreferences,
        updatePreference,
        resetToDefault,
        loading,
        themeMode,
        toggleThemeMode
      }}
    >
      {children}
    </PreferencesContext.Provider>
  );
};

export const usePreferences = () => useContext(PreferencesContext);